import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useRegisterSW } from "virtual:pwa-register/react"
import { RoutePathEnum } from "~Shared/router/RoutePath.enum"
import { NotificationService } from "~Shared/services"
import { NotificationTypeEnum } from "~Shared/services/notification.service"

const UPDATE_CHECK_INTERVAL_MS = 1000 * 60
// updateServiceWorker() reloads once the new worker controls the page; a page that is not
// controlled by a service worker never gets that event, so reload it ourselves
const RELOAD_FALLBACK_MS = 1000 * 10

const determineUpdateTimeInSec = () => {
    switch (window.location.pathname) {
        case RoutePathEnum.ROOT:
        case RoutePathEnum.LOGIN:
            return 5
        default:
            return 30
    }
}

export const useAutoUpdate = () => {
    // show message to user if new version is available and schedule auto-reload
    const { t } = useTranslation()
    const {
        needRefresh: [needRefresh],
        updateServiceWorker,
    } = useRegisterSW({
        onRegisteredSW: (swUrl, registration) => {
            console.debug(`[SW] Registered serviceWorker with url ${swUrl}`)
            if (!registration) {
                return
            }

            setInterval(() => {
                console.debug(`[SW] Checking for updates`)
                void registration.update()
            }, UPDATE_CHECK_INTERVAL_MS)
        },
        onRegisterError: (error) => {
            console.error(`[SW] Error during service worker registration: ${error}`)
        },
    })

    useEffect(() => {
        if (!needRefresh) {
            return
        }

        const updateTimeInSec = determineUpdateTimeInSec()
        console.debug(`[SW] Update available, reloading in ${updateTimeInSec}s`)

        NotificationService.send(
            NotificationTypeEnum.INFO,
            t("notification:new_version.title"),
            t("notification:new_version.message", { time: updateTimeInSec }),
            updateTimeInSec * 1000,
        )

        let reloadFallbackTimeoutId: ReturnType<typeof setTimeout>

        const updateTimeoutId = setTimeout(() => {
            void updateServiceWorker()
            reloadFallbackTimeoutId = setTimeout(() => window.location.reload(), RELOAD_FALLBACK_MS)
        }, updateTimeInSec * 1000)

        return () => {
            clearTimeout(updateTimeoutId)
            clearTimeout(reloadFallbackTimeoutId)
        }
    }, [needRefresh, updateServiceWorker, t])
}
